/**
 * Valma — Configurateur de collier (Alpine.js + Canvas), approche photo.
 *
 * Le rendu compose une PHOTO de collier détourée (par taille + couleur) avec une
 * breloque superposée, accrochée au fermoir. Recette (cf. tools/compose.py) :
 *   1. ancrage pré-calculé du collier : cx, yb, rw (fractions de l'image)
 *   2. hauteur breloque = rw_px * breloqueRatio
 *   3. position : haut de la breloque à yb_px - overlap*hauteur, centré sur cx_px
 *      => la bélière s'imbrique dans le bas de l'anneau (effet « accroché »).
 *
 * État synchronisé dans l'URL (?t=&c=&b=). À l'ajout au panier : bake PNG ->
 * upload R2 (Worker) -> /cart/add.js avec properties.
 *
 * Dépendances chargées avant : window.VALMA_DATA (généré) et window.VALMA_CONFIG (Liquid).
 */
function valmaConfigurator() {
  return {
    data: window.VALMA_DATA,
    cfg: window.VALMA_CONFIG,

    taille: (window.VALMA_DATA.tailles[0] && window.VALMA_DATA.tailles[0].id) || null,
    couleur: window.VALMA_DATA.couleurs[0].id,
    breloque: window.VALMA_DATA.breloques[0].id,

    loading: false,
    shared: false,
    error: '',
    // Vue active de la galerie : 'config' = rendu live du canvas, sinon URL d'une photo produit.
    activeView: 'config',
    // Snapshot du canvas, utilisé comme image de la 1ʳᵉ miniature (mise à jour à chaque rendu).
    configThumb: '',
    imageCache: new Map(),
    _renderSeq: 0,

    init() {
      this.hydrateFromUrl();
      this.preloadAll();
      this.render();
      this.$watch('taille', () => { this.render(); this.syncUrl(); });
      this.$watch('couleur', () => { this.render(); this.syncUrl(); });
      this.$watch('breloque', () => { this.render(); this.syncUrl(); });
    },

    // --- Getters dérivés ---------------------------------------------------
    get currentTaille() {
      if (!this.data.tailles.length) return { label: null, priceDelta: 0 };
      return this.data.tailles.find((t) => t.id === this.taille) || this.data.tailles[0];
    },
    get currentColor() {
      return this.data.couleurs.find((c) => c.id === this.couleur) || this.data.couleurs[0];
    },
    get currentBreloque() {
      return this.data.breloques.find((b) => b.id === this.breloque) || this.data.breloques[0];
    },
    get currentCollier() {
      const key = this.taille ? `${this.taille}-${this.couleur}` : this.couleur;
      return this.data.colliers[key];
    },
    get unavailable() {
      const c = this.currentCollier;
      return !c || c.missing === true;
    },
    get totalPrice() {
      return this.data.basePrice + this.currentTaille.priceDelta + this.currentBreloque.priceDelta;
    },
    get configString() {
      const parts = [];
      if (this.taille) parts.push(`t=${this.taille}`);
      parts.push(`c=${this.couleur}`, `b=${this.breloque}`);
      return parts.join('&');
    },

    // --- Helpers -----------------------------------------------------------
    assetUrl(filename) {
      const v = this.cfg.assetVersion ? '?' + this.cfg.assetVersion : '';
      return this.cfg.assetBase + filename + v;
    },
    formatPrice(cents) {
      return (cents / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
    },
    select(key, value) { this[key] = value; },

    // --- Carrousel galerie -------------------------------------------------
    // Liste ordonnée des vues : 'config' (canvas live) puis chaque photo produit.
    get galleryViews() {
      const imgs = (this.cfg.productImages || []).map((i) => i.url);
      return ['config', ...imgs];
    },
    get viewIndex() {
      const i = this.galleryViews.indexOf(this.activeView);
      return i < 0 ? 0 : i;
    },
    goToView(i) {
      const views = this.galleryViews;
      this.activeView = views[(i + views.length) % views.length];
    },
    nextView() { this.goToView(this.viewIndex + 1); },
    prevView() { this.goToView(this.viewIndex - 1); },
    // Swipe tactile sur l'image principale
    _touchX: null,
    onTouchStart(e) { this._touchX = e.changedTouches[0].clientX; },
    onTouchEnd(e) {
      if (this._touchX === null) return;
      const dx = e.changedTouches[0].clientX - this._touchX;
      if (Math.abs(dx) > 40) { dx < 0 ? this.nextView() : this.prevView(); }
      this._touchX = null;
    },

    hydrateFromUrl() {
      const p = new URLSearchParams(location.search);
      const t = p.get('t'), c = p.get('c'), b = p.get('b');
      if (t && this.data.tailles.some((x) => x.id === t)) this.taille = t;
      if (c && this.data.couleurs.some((x) => x.id === c)) this.couleur = c;
      if (b && this.data.breloques.some((x) => x.id === b)) this.breloque = b;
    },

    syncUrl() {
      const params = {};
      if (this.taille) params.t = this.taille;
      params.c = this.couleur;
      params.b = this.breloque;
      const p = new URLSearchParams(params);
      history.replaceState(null, '', `${location.pathname}?${p}`);
    },

    async copyShareUrl() {
      try {
        await navigator.clipboard.writeText(location.href);
        this.shared = true;
        setTimeout(() => { this.shared = false; }, 2000);
      } catch {
        this.error = 'Impossible de copier le lien.';
      }
    },

    // --- Images ------------------------------------------------------------
    loadImage(src) {
      if (this.imageCache.has(src)) return this.imageCache.get(src);
      const promise = new Promise((resolve, reject) => {
        const i = new Image();
        i.crossOrigin = 'anonymous';
        i.onload = () => resolve(i);
        i.onerror = () => reject(new Error(`Failed to load ${src}`));
        i.src = src;
      });
      this.imageCache.set(src, promise);
      return promise;
    },

    preloadAll() {
      const urls = [
        ...Object.values(this.data.colliers).filter((c) => !c.missing).map((c) => this.assetUrl(c.img)),
        ...this.data.breloques.map((b) => this.assetUrl(b.img)),
      ];
      urls.forEach((url) => this.loadImage(url).catch(() => {}));
    },

    // --- Rendu canvas ------------------------------------------------------
    async render() {
      const seq = ++this._renderSeq;
      const canvas = this.$refs.canvas;
      const ctx = canvas.getContext('2d');
      const { canvasHeightFactor, breloqueRatio, overlap, background } = this.data.render;

      const collier = this.currentCollier;
      if (this.unavailable) {
        // Combinaison sans visuel (ex collier pas encore fourni) : placeholder.
        canvas.width = 1000; canvas.height = Math.round(1000 * canvasHeightFactor);
        ctx.fillStyle = background; ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#bbb'; ctx.textAlign = 'center';
        ctx.font = '600 34px system-ui, sans-serif';
        ctx.fillText('Visuel bientôt disponible', canvas.width / 2, canvas.height / 2);
        this.configThumb = canvas.toDataURL('image/png');
        return;
      }

      let collierImg, brelImg;
      try {
        [collierImg, brelImg] = await Promise.all([
          this.loadImage(this.assetUrl(collier.img)),
          this.loadImage(this.assetUrl(this.currentBreloque.img)),
        ]);
      } catch (e) {
        console.error('Render failed', e);
        return;
      }
      if (seq !== this._renderSeq) return; // un rendu plus récent a démarré

      const W = collierImg.naturalWidth;
      const H = collierImg.naturalHeight;
      canvas.width = W;
      canvas.height = Math.round(H * canvasHeightFactor);

      ctx.fillStyle = background;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(collierImg, 0, 0, W, H);

      // Ancrage + breloque
      const cx = collier.cx * W;
      const yb = collier.yb * H;
      const rw = collier.rw * W;
      const brelH = rw * (this.currentBreloque.ratio || breloqueRatio);
      const brelW = (brelImg.naturalWidth / brelImg.naturalHeight) * brelH;
      ctx.drawImage(brelImg, cx - brelW / 2, yb - overlap * brelH, brelW, brelH);
      this.configThumb = canvas.toDataURL('image/png');
    },

    // --- Add to cart -------------------------------------------------------
    async addToCart() {
      if (this.unavailable) { this.error = 'Cette combinaison n’est pas encore disponible.'; return; }
      this.loading = true;
      this.error = '';
      const qtyEl = this.$refs.qtyInput;
      const quantity = Math.max(1, parseInt(qtyEl && qtyEl.value, 10) || 1);
      // Variante = la taille choisie (porte le bon prix : Jumbo 159 / Big Jumbo 169).
      const variantId = (this.cfg.variants && this.cfg.variants[this.currentTaille.label]) || this.cfg.productVariantId;
      try {
        await this.render();
        const canvas = this.$refs.canvas;
        const blob = await new Promise((r) => canvas.toBlob(r, 'image/png'));
        if (!blob) throw new Error("Impossible de générer l'aperçu");

        const uploadRes = await fetch(this.cfg.uploadEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'image/png', 'X-Config': this.configString },
          body: blob,
        });
        if (!uploadRes.ok) throw new Error('Upload de l’aperçu échoué');
        const { url: previewUrl } = await uploadRes.json();

        const cartRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: variantId,
            quantity: quantity,
            properties: {
              ...(this.currentTaille.label ? { Taille: this.currentTaille.label } : {}),
              Couleur: this.currentColor.label,
              ...(this.currentColor.precommande ? { 'Disponibilité': 'Pré-commande' } : {}),
              Breloque: this.currentBreloque.label,
              _config: this.configString,
              _preview: previewUrl,
            },
          }),
        });
        if (!cartRes.ok) throw new Error('Ajout au panier échoué');
        window.location.href = '/cart';
      } catch (e) {
        console.error(e);
        this.error = e.message;
      } finally {
        this.loading = false;
      }
    },
  };
}